import type { JSX, PropsWithChildren, ReactNode } from 'react';
import { PopoverProvider } from '@atlaskit/spotlight/popover-provider';
import { PopoverTarget } from '@atlaskit/spotlight/popover-target';
import { createPlacementFrontend } from '@post-office/message-maker-browser/create-placement-frontend';
import type {
	PlacementMessageFrontendFactory,
	AnyFrontendStage,
	DefaultMessagesResponse,
} from '@post-office/message-maker-browser/types';
import { usePlacementApi } from '@post-office/use-placement-api';
import { usePostOfficeContextScope } from '@atlassian/post-office-context/post-office-context-scope';

const createFrontend = createPlacementFrontend({
	placementId: 'jira-plans-release-date',
});
const scopes = [
	{
		name: 'jira/plan/releases',
		when: {
			context: { product: 'jira' },
			route: { pathname: { matches: '^/jira/plans/[^/]+/scenarios/[^/]+/releases/?$' } },
		},
	},
];
// Keep the host input outside the message pipeline so eligibility and dismissal never remount it.
export const ReleaseDateSpotlightTarget = ({
	children,
	message,
}: PropsWithChildren<{ message?: ReactNode }>): JSX.Element => (
	<PopoverProvider>
		<PopoverTarget>{children}</PopoverTarget>
		{message}
	</PopoverProvider>
);

export const PlacementFrontend: PlacementMessageFrontendFactory<
	{ render: AnyFrontendStage },
	PropsWithChildren<{ hasExternalTarget?: boolean }>
> = createFrontend<PropsWithChildren<{ hasExternalTarget?: boolean }>>(
	(Messages) =>
		({ children, hasExternalTarget = false }) => {
			const activeScopes = usePostOfficeContextScope({ scopes, treatIntersectionAsSame: true });
			const scope = activeScopes.current[0]?.name ?? '';
			const { data, error, isLoading } = usePlacementApi<DefaultMessagesResponse>('/', {
				queryParams: { activeScopes: scope },
				autoRefetch: { isEnabled: Boolean(scope), updateKey: scope },
			});
			const message = !error && !isLoading && scope ? data?.messages[0] : undefined;
			if (hasExternalTarget) {
				return message ? <Messages.render {...message} /> : null;
			}
			return (
				<ReleaseDateSpotlightTarget message={message ? <Messages.render {...message} /> : null}>
					{children}
				</ReleaseDateSpotlightTarget>
			);
		},
);
